import { useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Layers } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import { TipoIcon, TIPO_ICONOS } from "@/components/rastreo/TipoIcon";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { ErrorState } from "@/components/common/ErrorState";

const ICONOS_DISPONIBLES = Object.keys(TIPO_ICONOS);

export default function Tipos() {
  usePageTitle("Tipos de unidad");
  const queryClient = useQueryClient();
  const [dialogo, setDialogo] = useState(null); // { modo: 'nuevo'|'editar', tipo? }
  const [confirmarEliminar, setConfirmarEliminar] = useState(null);

  const { data: tipos, isLoading, isError, refetch } = useQuery({
    queryKey: ["tipos"],
    queryFn: () => adminApi.get("/tipos").then((r) => r.data),
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ["tipos"] });

  const eliminarMutation = useMutation({
    mutationFn: (tipo) => adminApi.delete(`/admin/tipos/${tipo.id}`),
    onSuccess: () => { toast.success("Tipo eliminado"); invalidar(); },
    onError: (err) => toast.error(formatApiError(err)),
    onSettled: () => setConfirmarEliminar(null),
  });

  return (
    <div className="mx-auto max-w-3xl px-8 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--tc-text)]">Tipos de unidad</h1>
          <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">
            Camión y maquinaria vienen precargados. Agregá los que necesites: aparecen
            automáticamente en los selectores y filtros de toda la app.
          </p>
        </div>
        <button
          data-testid={ADMIN.tiposNewButton}
          onClick={() => setDialogo({ modo: "nuevo" })}
          className="inline-flex shrink-0 items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
        >
          <Plus size={16} /> Nuevo tipo
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[var(--tc-accent)]" size={22} /></div>
        ) : isError ? (
          <ErrorState testId="admin-tipos-error" mensaje="No se pudieron cargar los tipos." onRetry={refetch} />
        ) : tipos.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <Layers size={26} className="text-[var(--tc-state-pending)]" />
            <p className="mt-3 text-sm text-[var(--tc-text-secondary)]">Todavía no hay tipos de unidad.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--tc-border)]">
            {tipos.map((t) => (
              <div key={t.id} data-testid={ADMIN.tiposRow} className="flex items-center justify-between gap-4 p-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
                    <TipoIcon icono={t.icono} size={16} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-[var(--tc-text)]">{t.nombre}</p>
                    <p className="mt-0.5 font-mono text-xs text-[var(--tc-text-secondary)]">{t.id}</p>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <button data-testid={ADMIN.tiposEditButton} onClick={() => setDialogo({ modo: "editar", tipo: t })} className="rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] hover:text-[var(--tc-accent)]">
                    <Pencil size={15} />
                  </button>
                  <button data-testid={ADMIN.tiposDeleteButton} onClick={() => setConfirmarEliminar(t)} className="rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-red-50 hover:text-red-600">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {dialogo && (
        <TipoDialog dialogo={dialogo} onClose={() => setDialogo(null)} onSaved={() => { setDialogo(null); invalidar(); }} />
      )}

      <AlertDialog open={Boolean(confirmarEliminar)} onOpenChange={(v) => !v && setConfirmarEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar "{confirmarEliminar?.nombre}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Si hay unidades cargadas con este tipo, no se podrá eliminar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.confirmDialogCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid={ADMIN.confirmDialogConfirm} onClick={() => eliminarMutation.mutate(confirmarEliminar)}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TipoDialog({ dialogo, onClose, onSaved }) {
  const tipo = dialogo.tipo;
  const [nombre, setNombre] = useState(tipo?.nombre || "");
  const [icono, setIcono] = useState(tipo?.icono || ICONOS_DISPONIBLES[0]);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      if (dialogo.modo === "editar") {
        await adminApi.put(`/admin/tipos/${tipo.id}`, { nombre, icono });
      } else {
        await adminApi.post("/admin/tipos", { nombre, icono });
      }
      toast.success("Tipo guardado");
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogo.modo === "editar" ? "Editar tipo" : "Nuevo tipo de unidad"}</DialogTitle>
          <DialogDescription>Aparece en los selectores de tipo de toda la app.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Nombre</label>
            <input data-testid={ADMIN.tipoFormNombre} required value={nombre} onChange={(e) => setNombre(e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Ícono</label>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
                <TipoIcon icono={icono} size={18} />
              </span>
              <select
                data-testid={ADMIN.tipoFormIcono}
                value={icono}
                onChange={(e) => setIcono(e.target.value)}
                className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
              >
                {ICONOS_DISPONIBLES.map((clave) => <option key={clave} value={clave}>{clave}</option>)}
              </select>
            </div>
          </div>
          {error && <p data-testid={ADMIN.tipoFormError} className="rounded-[var(--tc-radius)] bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <button data-testid={ADMIN.tipoFormSubmit} type="submit" disabled={guardando} className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
